import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, FileText, Loader2, Zap } from "lucide-react";
import { Seo } from "../components/Seo";
import { listHistory, downloadHistoryItem, deleteHistoryItem, errorMessage } from "../lib/api";
import { getTool } from "../data/tools";
import { usePro } from "../context/ProContext";

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function toolLabel(slug) {
    if (!slug) return null;
    if (slug === "fluxo") return "Fluxo de PDF";
    return getTool(slug)?.name || null;
}

export default function HistoricoPage() {
    const { isPro, checking } = usePro();
    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);

    const load = () => {
        listHistory()
            .then((d) => setItems(d.items))
            .catch((e) => setError(errorMessage(e)));
    };

    useEffect(() => {
        if (isPro) load();
    }, [isPro]);

    const remove = async (id) => {
        try {
            await deleteHistoryItem(id);
            setItems((prev) => prev.filter((i) => i.id !== id));
        } catch (e) {
            setError(errorMessage(e));
        }
    };

    return (
        <main className="py-14 px-6 max-w-3xl mx-auto min-h-[60vh]">
            <Seo
                title="Meu histórico — ScorpionPDF Pro"
                description="Arquivos salvos na sua conta Pro, disponíveis para baixar quando quiser."
                noindex
            />
            <span className="font-mono-tag text-xs font-bold text-[#0284C7] uppercase">Plano Pro</span>
            <h1 className="font-poster text-5xl md:text-6xl text-black mt-1">Meu histórico</h1>

            {checking ? (
                <p className="mt-10 font-mono-tag text-sm text-gray-600 flex items-center gap-2" data-testid="history-checking">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Verificando sua assinatura…
                </p>
            ) : !isPro ? (
                <div className="mt-10 border-2 border-black bg-black text-white p-10 text-center hard-shadow" data-testid="history-pro-required">
                    <Zap className="w-10 h-10 text-[#22D3EE] mx-auto mb-4" aria-hidden="true" />
                    <p className="font-poster text-2xl">RECURSO DO PLANO PRO</p>
                    <p className="text-sm text-gray-300 mt-2 max-w-md mx-auto">
                        Com o Pro, você salva resultados no seu histórico e baixa quando quiser — sem a regra dos 60
                        minutos.
                    </p>
                    <Link
                        to="/planos"
                        data-testid="history-plans-link"
                        className="inline-block mt-6 px-8 py-4 bg-[#0284C7] hover:bg-[#22D3EE] text-white font-poster text-xl tracking-wide transition-colors"
                    >
                        CONHECER O PRO
                    </Link>
                </div>
            ) : (
                <>
                    <p className="text-gray-700 mt-4">
                        Arquivos que você escolheu salvar. <strong>Atenção:</strong> itens do histórico não são
                        excluídos automaticamente — apague aqui o que não precisar mais.
                    </p>
                    {error && (
                        <p className="mt-4 border-2 border-black bg-[#FEF2F2] p-3 text-sm text-red-800" role="alert" data-testid="history-error">{error}</p>
                    )}
                    {items === null ? (
                        <p className="mt-8 font-mono-tag text-sm text-gray-600 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Carregando…
                        </p>
                    ) : items.length === 0 ? (
                        <div className="mt-8 border-2 border-black bg-white p-10 text-center" data-testid="history-empty">
                            <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" aria-hidden="true" />
                            <p className="font-poster text-2xl text-black">NADA SALVO AINDA</p>
                            <p className="text-sm text-gray-600 mt-2">
                                Processe um PDF e clique em “Salvar no histórico” na tela de resultado.
                            </p>
                            <Link to="/ferramentas" className="inline-block mt-5 px-6 py-3 bg-[#0284C7] hover:bg-black text-white font-poster text-lg transition-colors" data-testid="history-tools-link">
                                ABRIR FERRAMENTAS
                            </Link>
                        </div>
                    ) : (
                        <ul className="mt-8 space-y-3" data-testid="history-list">
                            {items.map((item) => (
                                <li key={item.id} className="border-2 border-black bg-white p-4 flex items-center gap-3 flex-wrap" data-testid={`history-item-${item.id}`}>
                                    <FileText className="w-6 h-6 text-[#0284C7] flex-shrink-0" aria-hidden="true" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-sm text-black truncate">{item.original_name}</p>
                                        <p className="font-mono-tag text-[11px] text-gray-500">
                                            {toolLabel(item.tool) ? `${toolLabel(item.tool)} · ` : ""}
                                            {formatBytes(item.size)} · {new Date(item.created_at).toLocaleString("pt-BR")}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => downloadHistoryItem(item)}
                                        data-testid={`history-download-${item.id}`}
                                        aria-label={`Baixar ${item.original_name}`}
                                        className="p-2.5 border-2 border-black bg-[#0284C7] text-white hover:bg-black transition-colors"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => remove(item.id)}
                                        data-testid={`history-delete-${item.id}`}
                                        aria-label={`Apagar ${item.original_name} do histórico`}
                                        className="p-2.5 border-2 border-black bg-white hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </main>
    );
}
